import { TCustomer, TSmartVariableDefinition } from '@/types/TSmartCRM';
import { EActionType } from 'src/enums/ESmartCRM';
import { showSuccessNotification, showErrorNotification } from '@/utils/SmartCRM/fieldUtils';

export const createCustomerHandlers = (
	editingCustomer: TCustomer | null,
	createCustomer: (data: Omit<TCustomer, 'id'>) => Promise<any>,
	updateCustomer: (id: string, data: Omit<TCustomer, 'id'>) => Promise<any>,
	deleteCustomer: (id: string) => Promise<any>,
	setShowModal: (show: boolean) => void,
	setEditingCustomer: (customer: TCustomer | null) => void,
	createBulkCustomers?: (data: Array<Omit<TCustomer, 'id'>>) => Promise<{
		createdLeads: TCustomer[];
		failedLeads: Array<{
			index: number;
			error: string;
			data: any;
		}>;
	}>,
) => {
	const handleSave = async (customerData: Omit<TCustomer, 'id'>) => {
		try {
			if (editingCustomer) {
				await updateCustomer(editingCustomer.id, customerData);
				showSuccessNotification('Customer updated successfully');
			} else {
				await createCustomer(customerData);
				showSuccessNotification('Customer created successfully');
			}
			setShowModal(false);
			setEditingCustomer(null);
		} catch (error: any) {
			showErrorNotification(
				editingCustomer ? 'Error updating customer' : 'Error creating customer',
				error?.message,
			);
		}
	};

	const handleBulkSave = async (bulkCustomersData: Array<Omit<TCustomer, 'id'>>) => {
		if (!createBulkCustomers) {
			showErrorNotification('Bulk import not available', 'Bulk customer creation is not supported');
			return;
		}

		try {
			const result = await createBulkCustomers(bulkCustomersData);

			const { createdLeads, failedLeads } = result;
			const totalLeads = bulkCustomersData.length;
			const successCount = createdLeads.length;
			const failureCount = failedLeads.length;

			if (successCount === totalLeads) {
				showSuccessNotification(
					'Bulk import completed successfully',
					`${successCount} customers created successfully`,
				);
			} else if (successCount > 0) {
				showSuccessNotification(
					'Bulk import partially completed',
					`${successCount} customers created successfully, ${failureCount} failed`,
				);
			} else {
				showErrorNotification(
					'Bulk import failed',
					`Failed to create any customers. ${failureCount} errors occurred.`,
				);
			}

			setShowModal(false);
			setEditingCustomer(null);
		} catch (error: any) {
			showErrorNotification(
				'Error during bulk import',
				error?.message || 'An unexpected error occurred during bulk import',
			);
		}
	};

	const handleAction = async (action: EActionType, customer: TCustomer) => {
		switch (action) {
			case EActionType.CALL:
				showSuccessNotification(`Calling ${customer.name}`, `Dialing ${customer.number}...`);
				break;
			case EActionType.EDIT:
				setEditingCustomer(customer);
				setShowModal(true);
				break;
			case EActionType.DELETE:
				try {
					await deleteCustomer(customer.id);
					showSuccessNotification('Customer deleted successfully');
				} catch (error: any) {
					showErrorNotification('Error deleting customer', error?.message);
				}
				break;
		}
	};

	const handleNew = () => {
		setEditingCustomer(null);
		setShowModal(true);
	};

	const handleClose = () => {
		setShowModal(false);
		setEditingCustomer(null);
	};

	return {
		handleSave,
		handleBulkSave,
		handleAction,
		handleNew,
		handleClose,
	};
};

export const createFieldHandlers = (
	editingField: TSmartVariableDefinition | null,
	saveField: (data: Partial<TSmartVariableDefinition>) => Promise<any>,
	deleteField: (id: string) => Promise<any>,
	setShowModal: (show: boolean) => void,
	setEditingField: (field: TSmartVariableDefinition | null) => void,
	setDeletingFieldId: (id: string | null) => void,
) => {
	const handleSave = async (fieldData: TSmartVariableDefinition) => {
		try {
			await saveField(fieldData as Partial<TSmartVariableDefinition>);
			setShowModal(false);
			setEditingField(null);
			showSuccessNotification(
				editingField ? 'Field updated successfully' : 'Field created successfully',
			);
		} catch (error: any) {
			showErrorNotification(
				editingField ? 'Error updating field' : 'Error creating field',
				error?.message,
			);
		}
	};

	const handleDelete = async (fieldId: string) => {
		try {
			setDeletingFieldId(fieldId);
			await deleteField(fieldId);
			showSuccessNotification('Field deleted successfully');
		} catch (error: any) {
			showErrorNotification('Error deleting field', error?.message);
		} finally {
			setDeletingFieldId(null);
		}
	};

	const handleAdd = () => {
		setEditingField(null);
		setShowModal(true);
	};

	const handleEdit = (field: TSmartVariableDefinition) => {
		setEditingField(field);
		setShowModal(true);
	};

	const handleClose = () => {
		setShowModal(false);
		setEditingField(null);
	};

	return { handleSave, handleDelete, handleAdd, handleEdit, handleClose };
};
